'use strict';

/**
 * Base API URL resolved from env with a localhost fallback.
 */
export const API_BASE = (process.env.MEDICAREPRO_API_URL as string) || 'http://localhost/api';

/**
 * Common pagination/filter options accepted by API requests.
 */
export type APIOptions<TCriteria = unknown, TInclude extends string = string> = {
  /** Maximum number of records to return. */
  limit?: number;
  /** Starting offset for records. */
  offset?: number;
  /** Abort signal to cancel the request. */
  signal?: AbortSignal;
  /** Optional criteria payload to filter results. */
  criteria?: TCriteria;
  /** Array of what to include in the response */
  include?: Array<TInclude>;
};

/**
 * Operators usable in criteria filtering.
 */
export type APICriteriaOption<T = unknown> = {
  /** Equals comparison. */
  eq?: T;
  /** Not-equals comparison. */
  neq?: T;
  /** Inclusion list comparison. */
  in?: T[];
  /** Matches when the field is null. */
  isNull?: boolean;
  /** Matches when the field is not null. */
  isNotNull?: boolean;
  /** Value is between two values */
  between?: {
    from: string;
    to: string;
  };
};

/**
 * A field criterion can be a direct value or an operator object.
 */
export type APICriteriaValue<T> = T | APICriteriaOption<T>;

/**
 * Criteria map keyed by payload property names.
 */
export type APICriteria<TPayload> = {
  [K in keyof TPayload]?: APICriteriaValue<NonNullable<TPayload[K]>>;
};

/**
 * Core API wrapper with shared request-building utilities.
 */
export class CoreAPI {
  protected baseUrl: string = API_BASE + '/v1';
  protected clientLocation: string;
  protected clientLanguage: string;
  protected globalParameters: object = {};
  protected accessToken: string;

  /** Returns the base API URL including version. */
  public getBaseURL() {
    return this.baseUrl;
  }

  /** Returns the currently configured bearer token. */
  public getAccessToken() {
    return this.accessToken;
  }

  /** Sets the bearer token to include on outbound requests. */
  public setAccessToken(accessToken: string) {
    this.accessToken = accessToken;
    return this;
  }

  /**
   * Adds or removes a global parameter that is appended to each request.
   * Passing a null value removes the parameter.
   */
  protected setGlobalParameter(key: string, value: any) {
    if (typeof value === null) {
      delete this.globalParameters[key];
    } else {
      this.globalParameters[key] = value;
    }

    return this;
  }

  /** Returns all globally configured parameters. */
  protected getGlobalParameters() {
    return this.globalParameters;
  }

  /**
   * Serializes an object into a query string, preserving nested keys.
   */
  public buildQuery(formdata: object, numeric_prefix: string = null, arg_separator: string = null) {
    let value,
      key,
      tmp = [],
      that = this;

    const _http_build_query_helper = function (
      key: string,
      val: any,
      arg_separator: string = null
    ) {
      let k,
        tmp = [];
      if (val === true) {
        val = '1';
      } else if (val === false) {
        val = '0';
      }

      if (val != null) {
        if (typeof val === 'object') {
          for (k in val) {
            if (val[k] != null && typeof val[k] !== 'function') {
              tmp.push(_http_build_query_helper(key + '[' + k + ']', val[k], arg_separator));
            }
          }

          return tmp.join(arg_separator);
        } else if (typeof val !== 'function') {
          return encodeURIComponent(key) + '=' + encodeURIComponent(val);
        } else {
          throw new Error('There was an error processing your object.');
        }
      } else {
        return '';
      }
    };

    if (!arg_separator) {
      arg_separator = '&';
    }

    for (key in formdata) {
      value = formdata[key];
      if (numeric_prefix && !isNaN(key)) {
        key = String(numeric_prefix) + key;
      }
      const query = _http_build_query_helper(key, value, arg_separator);
      if (query != '') {
        tmp.push(query);
      }
    }

    return tmp.join(arg_separator);
  }

  /**
   * Issues an HTTP request using Fetch and returns the parsed response.
   *
   * @param path API path beginning with "/".
   * @param parameters Query parameters to append to the request URL.
   * @param custom_options Fetch options; supports method, body, signal, and responseType overrides.
   */
  public sendRequest(path: string, parameters: object = {}, custom_options: any = null) {
    var parameters = parameters ? parameters : {};

    const compiledParameters = Object.assign(parameters, this.getGlobalParameters());

    if (this.accessToken) {
      //headers.append('Authorization', 'Bearer ' + this.accessToken);
      compiledParameters['accessToken'] = this.accessToken;
    }

    let requestUrl = this.baseUrl + path;
    requestUrl +=
      compiledParameters && Object.keys(compiledParameters).length > 0
        ? '?' + this.buildQuery(compiledParameters)
        : '';

    let options;
    const headers = new Headers();

    if (
      custom_options &&
      custom_options.method &&
      (custom_options.method == 'PUT' ||
        custom_options.method == 'PATCH' ||
        custom_options.method == 'POST' ||
        custom_options.method == 'DELETE')
    ) {
      let body;

      if (custom_options.hasOwnProperty('bodyEncode') && custom_options.bodyEncode === false) {
        body = custom_options.body;
      } else {
        body = JSON.stringify(custom_options.body);
      }

      options = {
        mode: 'cors',
        body: body,
        method: custom_options.method,
        headers: headers,
      };
    } else {
      options = {
        mode: 'cors',
        method: 'GET',
        headers: headers,
      };
    }

    if (custom_options && custom_options.signal) {
      options.signal = custom_options.signal;
    }

    const optionsFinal: RequestInit = options;

    return fetch(requestUrl, optionsFinal)
      .then((response) => {
        if (custom_options?.responseType) {
          switch (custom_options.responseType) {
            case 'blob':
              return response.blob();
            case 'text':
              return response.text();
            default:
              return response.json();
          }
        } else {
          return response.json();
        }
      })
      .then((response) => {
        if (
          response.status &&
          (response.status.toString().substring(0, 1) === '5' ||
            response.status.toString().substring(0, 1) === '4')
        ) {
          throw new Error(response.message);
        }

        return response;
      });
  }
}
